// pages/UnitDetail.tsx — one flat's record (the demo's "20b. FLATS — one flat's record"): the state it is in, and tabs
// Overview (tiles, current tenancy, linked records, recent history, details), Changeover, Tenancies, Works, Marketing
// and Compliance. One read (GET /units/:id/record) fills everything but the works list; each tab shows only to a user
// who may read the page behind it. Editing the unit happens from the ⋯ menu.
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, BookOpen, ChevronRight, ClipboardCheck, LogIn, LogOut, Megaphone, ShieldCheck } from 'lucide-react';
import { api } from '../lib/api';
import { useCan } from '../hooks/usePermission';
import { arrearsApi } from '../lib/arrears';
import { depositsApi } from '../lib/deposits';
import { feesApi } from '../lib/fees';
import { Tabs, Card, Badge, Button, KeyValue, Money, PageHeader, DateText, SkeletonRows } from '../components/ui';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FilterMenu } from '../components/ui/FilterMenu';
import { StatTile } from '../components/charts/StatTile';
import { UnitForm } from '../components/forms/UnitForm';
import { ListingForm } from '../components/forms/ListingForm';
import { CertificateForm } from '../components/forms/CertificateForm';
import { Confirm } from '../components/forms/common';
import { WorkOrderForm } from '../components/workorders/WorkOrderForm';
import { UnitLettingsCard } from '../components/units/UnitLettingsCard';
import { UnitViewingsCard } from '../components/units/UnitViewingsCard';
import { StateBadge } from '../components/property/FlatsTab';
import { CertificateSheet } from '../components/property/ComplianceGridPanel';
import { flatsApi, floorLabel, typeLabel, FLAT_STATE_COLOUR } from '../lib/flats';
import { CELL_TONE, humanise, workRowColour } from '../lib/propertyManagement';
import { PHASE_INFO, type Phase } from '../lib/checklists';
import { formatDate, formatMoney } from '../lib/viz';
import type { FlatChecklistLink, FlatHistoryEvent, FlatRecord, FlatTenancy, WorkOrderRow } from '../types/api';
import { PageLoader } from '../components/BrandLoader';
import { ImagesTab } from '../components/media/ImagesTab';

type TabKey = 'overview' | 'changeover' | 'tenancies' | 'works' | 'marketing' | 'images' | 'compliance';
const HISTORY_ICON: Record<FlatHistoryEvent['kind'], string> = {
  TENANCY_STARTED: 'var(--status-good)', TENANCY_ENDED: 'var(--status-neutral)', WORK_RAISED: 'var(--status-warning)', WORK_COMPLETED: 'var(--status-good)',
  CERTIFICATE_ISSUED: 'var(--color-sky-600)', CHECKLIST_COMPLETED: 'var(--color-violet-600)', LISTING_PUBLISHED: 'var(--color-bronze-600)',
};

export default function UnitDetailPage() {
  const { id: propertyId, unitId } = useParams<{ id: string; unitId: string }>();
  const navigate = useNavigate(); const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [recordingCert, setRecordingCert] = useState<string | null>(null); // a certificate type, or '' for any
  const can = {
    changeovers: useCan('changeovers.read'), tenancies: useCan('tenancies.read'), works: useCan('workOrders.read'), listings: useCan('listings.read'),
    viewings: useCan('viewings.read'), compliance: useCan('compliance.read'), media: useCan('media.read'), reportJob: useCan('workOrders.create'), recordCert: useCan('compliance.create'), giveNotice: useCan('tenancies.terminate'),
  };

  const { data: u } = useQuery({ queryKey: ['unit', unitId], queryFn: async () => (await api.get<Record<string, any>>(`/units/${unitId}`)).data, enabled: !!unitId });
  const record = useQuery({ queryKey: ['flat-record', unitId], queryFn: () => flatsApi.record(unitId!), enabled: !!unitId });
  const r = record.data;
  const back = `/app/properties/${propertyId}?tab=flats`;
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['unit', unitId] }); void qc.invalidateQueries({ queryKey: ['flat-record', unitId] }); void qc.invalidateQueries({ queryKey: ['flats', propertyId] }); void qc.invalidateQueries({ queryKey: ['/units'] }); };
  const remove = useMutation({ mutationFn: async () => api.delete(`/units/${unitId}`), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['/units'] }); void qc.invalidateQueries({ queryKey: ['flats', propertyId] }); navigate(back); } });

  const TABS = ([
    ['overview', 'Overview', true, null],
    ['changeover', 'Changeover', can.changeovers, r?.changeover ? `${r.changeover.nDone}/${r.changeover.steps.length}` : null],
    ['tenancies', 'Tenancies', can.tenancies, r?.tenancies ? String(r.tenancies.length) : null],
    ['works', 'Works', can.works, r?.tiles.openWorks ? String(r.tiles.openWorks) : null],
    ['marketing', 'Marketing', can.listings || can.viewings, r?.flat.listing?.status === 'PUBLISHED' ? 'live' : null],
    ['images', 'Images', can.media, null],
    ['compliance', 'Compliance', can.compliance, r?.tiles.certificates && (r.tiles.certificates.expired + r.tiles.certificates.dueSoon) ? String(r.tiles.certificates.expired + r.tiles.certificates.dueSoon) : null],
  ] as Array<[TabKey, string, boolean, string | null]>).filter((t) => t[2]);
  const tab: TabKey = TABS.find((t) => t[0] === params.get('tab'))?.[0] ?? 'overview';
  const setTab = (t: TabKey) => { const n = new URLSearchParams(params); if (t === 'overview') n.delete('tab'); else n.set('tab', t); n.delete('cert'); setParams(n, { replace: true }); };

  if (!u || !r) return record.isError ? <div className="py-12 text-center text-sm text-slate-400">This flat could not be loaded.</div> : <PageLoader />;
  const f = r.flat;
  const current = r.tenancies?.find((t) => t.current) ?? null;
  const colour = FLAT_STATE_COLOUR[f.state];
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader back={back} title={u.reference} menu={[
        { label: 'Report a job', icon: 'Wrench', can: 'workOrders.create', onClick: () => setReporting(true) },
        { label: 'Record a certificate', icon: 'ShieldCheck', can: 'compliance.create', onClick: () => setRecordingCert('') },
        !!current && !r.changeover && can.giveNotice && { label: 'Start changeover (give notice)', icon: 'DoorOpen', title: 'A changeover opens when notice is given on the tenancy', to: `/app/tenancies/${current.id}` },
        { label: 'Edit unit', icon: 'Pencil', can: 'units.update', separator: true, onClick: () => setEditing(true) },
        { label: 'Archive unit', icon: 'Trash2', can: 'units.delete', danger: true, onClick: () => setRemoving(true) },
      ]} />

      {/* The flat's header (the demo's fl-mhead). */}
      <div className="surface mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4" style={{ boxShadow: `inset 3px 0 0 ${colour}` }}>
        <span className="grid h-12 min-w-12 shrink-0 place-items-center rounded-lg px-2 font-mono text-[15px] font-semibold" style={{ color: colour, background: `color-mix(in oklab, ${colour} 12%, transparent)` }}>{f.reference.replace(/^[A-Z]+-0*/, '') || f.reference}</span>
        <div className="min-w-0 flex-1">
          <div className="text-[16px] font-semibold text-ink-900">{f.name && f.name !== f.reference ? `${f.name} · ${f.reference}` : f.reference}</div>
          <div className="text-[12.5px] text-ink-500">{[f.buildingName, floorLabel(f.floor), `${typeLabel(f.unitType)}${f.bedrooms ? ` · ${f.bedrooms} bed` : ''}`, f.areaSqft ? `${f.areaSqft.toLocaleString('en-GB')} sq ft` : null, `list ${formatMoney(f.marketRentMinor, f.currency)} pcm`].filter(Boolean).join(' · ')}</div>
        </div>
        <StateBadge state={f.state} label={f.stateLabel} large />
      </div>

      {TABS.length > 1 && (
        <Tabs className="mb-2.5" value={tab} onChange={setTab} items={TABS.map(([k, l, , n]) => ({ key: k, label: <>{l}{n && <span className={`rounded-full px-1.5 text-[10.5px] tabular-nums ${(k === 'works' || k === 'compliance') ? 'bg-red-50 text-red-600' : 'bg-ink-100 text-ink-500'}`}>{n}</span>}</> }))} />
      )}

      {tab === 'overview' && <OverviewTab r={r} u={u} propertyId={propertyId!} onTab={setTab} />}
      {tab === 'changeover' && <ChangeoverTab r={r} propertyId={propertyId!} />}
      {tab === 'tenancies' && <TenanciesTab r={r} />}
      {tab === 'works' && <WorksTab unitId={unitId!} />}
      {tab === 'marketing' && <MarketingTab r={r} u={u} propertyId={propertyId!} />}
      {tab === 'images' && <ImagesTab unitId={unitId!} />}
      {tab === 'compliance' && <ComplianceTab r={r} propertyId={propertyId!} focus={params.get('cert')} onFocus={(t) => { const n = new URLSearchParams(params); if (t) n.set('cert', t); else n.delete('cert'); setParams(n, { replace: true }); }} onRecord={can.recordCert ? (t) => setRecordingCert(t) : undefined} />}

      {editing && <UnitForm open unit={u} onClose={() => { setEditing(false); refresh(); }} />}
      {reporting && <WorkOrderForm open propertyId={propertyId} unitId={unitId} currency={u.marketRentCurrency} onClose={() => { setReporting(false); refresh(); void qc.invalidateQueries({ queryKey: ['/work-orders'] }); }} />}
      {recordingCert !== null && <CertificateForm open propertyId={propertyId} preset={{ unitId, certificateType: recordingCert || undefined }} onClose={() => { setRecordingCert(null); refresh(); void qc.invalidateQueries({ queryKey: ['compliance-grid', propertyId] }); }} />}
      <Confirm open={removing} onClose={() => setRemoving(false)} title={`Archive ${u.reference}?`}
        body="The unit and any advert on it are archived. A unit with a live tenancy cannot be removed — end the tenancy first."
        confirmLabel="Archive unit" pending={remove.isPending} error={remove.error} onConfirm={() => remove.mutate()} />
    </div>
  );
}

function SectionHead({ children, n }: { children: React.ReactNode; n?: number }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">{children}</h3>
      {n !== undefined && <span className="rounded-full bg-ink-100 px-1.5 text-[10.5px] font-medium text-ink-500">{n}</span>}
      <span className="h-px flex-1 bg-ink-100" />
    </div>
  );
}

/** One tenancy as a row (the demo's tenRowHTML). */
function TenancyLine({ t }: { t: FlatTenancy & { current?: boolean } }) {
  return (
    <Link to={`/app/tenancies/${t.id}`} className={`flex items-start gap-3 rounded-xl border px-4 py-3 transition hover:border-bronze-300 hover:bg-bronze-50/40 ${t.current ? 'border-emerald-200 bg-emerald-50/30' : 'border-ink-100'}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-[13.5px] font-medium text-ink-900">{t.tenants || t.reference}<Badge value={t.status} /></div>
        <div className="mt-0.5 text-[12px] text-ink-500">
          {t.startDate ? formatDate(t.startDate) : '—'} – {t.endDate ? formatDate(t.endDate) : t.status === 'ENDED' ? '—' : 'ongoing'} · {formatMoney(t.rentMinor, t.rentCurrency)} pcm{t.letBy ? ` · let by ${t.letBy}` : ''} · {t.reference}
        </div>
        {t.settingUp && <div className="mt-1 text-[11.5px] font-medium text-amber-700">Lotus Living to finish setting up{t.moveInDate ? ` · moves in on ${formatDate(t.moveInDate)}` : ''}</div>}
      </div>
      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-ink-300" />
    </Link>
  );
}

function LinkedRecord({ icon: I, title, sub, to, progress }: { icon: React.ComponentType<{ className?: string }>; title: string; sub: string; to: string | null; progress?: FlatChecklistLink | null }) {
  const body = (
    <>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-bronze-50 text-bronze-600"><I className="h-4 w-4" /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-ink-900">{title}</span>
        <span className="block truncate text-[11.5px] text-ink-500">{sub}</span>
        {progress && (
          <span className="mt-1.5 flex items-center gap-2">
            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-100"><span className="block h-full rounded-full" style={{ width: `${progress.percent}%`, background: progress.status === 'COMPLETE' ? 'var(--status-good)' : 'var(--status-warning)' }} /></span>
            <span className="shrink-0 text-[10.5px] tabular-nums text-ink-500">{progress.done}/{progress.total}</span>
          </span>
        )}
      </span>
      {to && <ArrowRight className="h-4 w-4 shrink-0 text-ink-300" />}
    </>
  );
  const cls = 'flex items-center gap-3 rounded-xl border border-ink-100 px-3.5 py-3 text-left';
  return to ? <Link to={to} className={`${cls} transition hover:border-bronze-300 hover:bg-bronze-50/40`}>{body}</Link> : <div className={`${cls} opacity-70`}>{body}</div>;
}

function OverviewTab({ r, u, propertyId, onTab }: { r: FlatRecord; u: Record<string, any>; propertyId: string; onTab: (t: TabKey) => void }) {
  const unitId = u.id as string;
  const f = r.flat;
  const canArrears = useCan('arrears.read'); const canDeposits = useCan('deposits.read'); const canResidents = useCan('residents.read');
  const canFees = useCan('fees.read'); const canBudgets = useCan('budgets.read'); const canChecklists = useCan('checklists.read'); const canReservations = useCan('reservations.read');
  const accounts = useQuery({ queryKey: ['property-accounts', propertyId], queryFn: () => feesApi.accounts(propertyId), enabled: canFees || canBudgets });
  const budget = accounts.data?.budget?.budget ? accounts.data.budget : null;
  const budgetUsed = budget?.byUnit.find((x) => x.unitId === unitId)?.actualMinor ?? 0;
  const arrears = useQuery({ queryKey: ['/arrears', { propertyId }], queryFn: () => arrearsApi.list({ propertyId }), enabled: canArrears });
  const owing = (arrears.data ?? []).filter((x) => x.unitId === unitId);
  const owed = owing.reduce((s, x) => s + x.owedMinor, 0);
  const current = r.tenancies?.find((t) => t.current) ?? null;
  const deposit = useQuery({ queryKey: ['deposit', 'tenancy', current?.id], queryFn: () => depositsApi.forTenancy(current!.id), enabled: !!current && canDeposits });
  const c = r.tiles.certificates;
  const certTotal = c ? c.valid + c.dueSoon + c.expired + c.notRecorded : 0;
  const tenancy = r.currentTenancy;
  const banner = r.changeover
    ? { colour: PHASE_INFO[r.changeover.phase as Phase]?.colour ?? 'var(--color-violet-600)', title: r.changeover.phaseLabel, line: `${r.changeover.nDone} of ${r.changeover.steps.length} steps done${r.changeover.next ? ` · next: ${r.changeover.next.toLowerCase()}` : ''}`, action: 'Open the changeover', tab: 'changeover' as const }
    : f.listing?.status === 'PUBLISHED'
      ? { colour: 'var(--color-bronze-600)', title: 'On the market', line: `Advertised at ${formatMoney(f.listing.advertisedRentMinor, f.listing.currency)} pcm${f.listing.availableFrom ? `, available ${formatDate(f.listing.availableFrom)}` : ''}`, action: 'Open the advert', tab: 'marketing' as const }
      : null;
  const notice = !tenancy && r.visible.tenancies
    ? f.state === 'COMING_UP' || f.state === 'LET_AGREED' ? (f.reservation ? `An application is on it — ${f.reservation.stageLabel.toLowerCase()}.` : 'A let is agreed — the tenancy opens once it is set up.')
      : f.state === 'OFF_MARKET' ? 'The flat is off the market.' : f.state === 'UNDER_CONSTRUCTION' ? 'The flat is still being built.' : 'The flat is vacant.'
    : null;
  const tenanciesForChecklist = r.tenancies?.find((t) => t.upcoming) ?? current;

  return (
    <div className="grid gap-2.5 lg:grid-cols-3">
      <div className="min-w-0 space-y-2.5 lg:col-span-2">
        <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-4">
          <StatTile compact label="Current rent" value={formatMoney(r.tiles.currentRentMinor, r.tiles.currency)} hint={current ? 'per month, this tenancy' : 'list rent per month'} />
          {r.tiles.tenanciesToDate !== null && <StatTile compact label="Tenancies to date" value={r.tiles.tenanciesToDate} onClick={() => onTab('tenancies')} />}
          {r.tiles.openWorks !== null && <StatTile compact label="Open works" value={r.tiles.openWorks} status={r.tiles.openWorks ? 'warning' : undefined} onClick={() => onTab('works')} />}
          {c && <StatTile compact label="Certificates" value={<>{c.valid}<span className="ml-1 text-[12px] font-medium text-ink-400">of {certTotal} in date</span></>} status={c.expired ? 'critical' : c.dueSoon || c.notRecorded ? 'warning' : undefined} onClick={() => onTab('compliance')} />}
        </div>

        {banner && (
          <div className="surface flex flex-wrap items-center gap-3 px-4 py-3" style={{ boxShadow: `inset 3px 0 0 ${banner.colour}` }}>
            <div className="min-w-0 flex-1"><div className="text-[13px] font-semibold" style={{ color: banner.colour }}>{banner.title}</div><div className="text-[12px] text-ink-600">{banner.line}</div></div>
            {r.changeover && <div className="flex gap-0.5" aria-hidden>{r.changeover.steps.map((s) => <i key={s.key} className="block h-1.5 w-4 rounded-full" style={{ background: s.done ? banner.colour : 'var(--color-ink-100)' }} />)}</div>}
            <Button size="sm" variant="secondary" onClick={() => onTab(banner.tab)}>{banner.action}</Button>
          </div>
        )}

        {r.visible.tenancies && (
          <section>
            <SectionHead>Current tenancy</SectionHead>
            {tenancy ? <TenancyLine t={tenancy} /> : <div className="surface px-4 py-4 text-[12.5px] text-ink-500">No live tenancy. {notice}</div>}
            {f.incoming && <p className="mt-2 text-[12px] text-ink-500">Next in: <Link to={`/app/tenancies/${f.incoming.id}`} className="font-medium text-ink-800 hover:underline">{f.incoming.leadTenantName ?? f.incoming.reference}</Link>{f.incoming.startDate ? ` from ${formatDate(f.incoming.startDate)}` : ''}</p>}
          </section>
        )}

        <section>
          <SectionHead>Linked records</SectionHead>
          <div className="grid gap-2 sm:grid-cols-2">
            {canChecklists && r.visible.tenancies && (
              <LinkedRecord icon={LogIn} title="Moving In" progress={r.linked.moveIn}
                sub={r.linked.moveIn ? (r.linked.moveIn.status === 'COMPLETE' ? `Completed${r.linked.moveIn.completedAt ? ` ${formatDate(r.linked.moveIn.completedAt)}` : ''}` : `Lotus Living to finish · ${r.linked.moveIn.total - r.linked.moveIn.done} left`) : tenanciesForChecklist ? 'Not opened yet — opens on the tenancy' : 'No tenancy to move in'}
                to={tenanciesForChecklist ? `/app/tenancies/${tenanciesForChecklist.id}?checklist=in` : null} />
            )}
            {canChecklists && r.visible.tenancies && (
              <LinkedRecord icon={LogOut} title="Moving Out" progress={r.linked.moveOut}
                sub={r.linked.moveOut ? (r.linked.moveOut.status === 'COMPLETE' ? 'Completed' : `Lotus Living to finish · ${r.linked.moveOut.total - r.linked.moveOut.done} left`) : current ? 'Opens when notice is given' : 'Nobody moving out'}
                to={r.linked.moveOut ? `/app/tenancies/${r.linked.moveOut.tenancyId}?checklist=out` : null} />
            )}
            {canReservations && (
              <LinkedRecord icon={BookOpen} title="Application" sub={r.linked.reservation ? r.linked.reservation.stageLabel : 'Nobody applying'} to={r.linked.reservation ? (r.linked.reservation.status === 'AVAILABLE' ? `/app/lettings/available?unit=${unitId}` : `/app/lettings/applications?flat=${r.linked.reservation.id}`) : null} />
            )}
            {r.visible.listings && (
              <LinkedRecord icon={Megaphone} title="Advert" sub={f.listing ? `${humanise(f.listing.status)} · ${formatMoney(f.listing.advertisedRentMinor, f.listing.currency)} pcm` : 'Not advertised'} to={f.listing ? `/app/listings/${f.listing.id}` : null} />
            )}
          </div>
        </section>

        <section>
          <SectionHead n={r.recentHistory.length}>Recent history</SectionHead>
          {r.recentHistory.length ? (
            <ul className="surface divide-y divide-ink-100 overflow-hidden">
              {r.recentHistory.map((h, i) => {
                const inner = (
                  <>
                    <i className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ background: HISTORY_ICON[h.kind] }} />
                    <span className="min-w-0 flex-1"><span className="block truncate text-[12.5px] font-medium text-ink-800">{h.title}</span>{h.detail && <span className="block truncate text-[11.5px] text-ink-500">{h.detail}</span>}</span>
                    <span className="shrink-0 text-[11.5px] tabular-nums text-ink-500">{formatDate(h.at)}</span>
                  </>
                );
                return (
                  <li key={`${h.kind}-${i}`}>
                    {h.link.path ? <Link to={h.link.path} className="flex items-start gap-3 px-4 py-2.5 transition hover:bg-bronze-50/40">{inner}</Link>
                      : <button type="button" onClick={() => h.link.tab && onTab(h.link.tab)} className="flex w-full items-start gap-3 px-4 py-2.5 text-left transition hover:bg-bronze-50/40">{inner}</button>}
                  </li>
                );
              })}
            </ul>
          ) : <div className="surface px-4 py-6 text-center text-[12.5px] text-ink-400">Nothing recorded against this flat yet.</div>}
        </section>
      </div>

      <div className="space-y-2.5">
        <Card title="Details" actions={<span className="flex items-center gap-1.5">{canArrears && current && (owed > 0 ? <Badge value={`In arrears · ${formatMoney(owed, owing[0]!.currency)}`} tone="critical" /> : <Badge value="Rent up to date" tone="good" />)}<Badge value={u.status} /></span>}>
          <KeyValue items={[
            { label: 'Property', value: <Link to={`/app/properties/${propertyId}`} className="text-bronze-700 hover:underline">{u.property?.name ?? '—'}</Link> },
            { label: 'Building', value: u.building?.name ?? 'None' },
            { label: 'Name', value: u.name || '—' },
            { label: 'Floor', value: floorLabel(u.floor) },
            { label: 'Bedrooms', value: u.bedrooms === 0 ? 'Studio' : u.bedrooms },
            { label: 'Bathrooms', value: u.bathrooms },
            { label: 'Area', value: u.areaSqft ? `${u.areaSqft} sq ft` : '—' },
            { label: 'Type', value: typeLabel(u.unitType) },
            { label: 'Furnished', value: u.isFurnished ? 'Yes' : 'No' },
            { label: 'Market rent', value: <Money minor={u.marketRentMinor} currency={u.marketRentCurrency} /> },
            { label: 'Features', value: (u.features ?? []).join(', ') || '—' },
          ]} />
        </Card>
        {current && (
          <Card title="Residents" subtitle="Who lives here now">
            <ul className="space-y-1.5">
              {/* The tenants come from the tenancy page; the record carries the names, so list them and link to the tenancy. */}
              {(current.tenants ? current.tenants.split(' & ') : [current.reference]).map((name) => (
                <li key={name} className="flex items-center justify-between gap-2 text-[13px]">
                  <Link to={canResidents ? `/app/contacts?sub=residents&search=${encodeURIComponent(name)}` : `/app/tenancies/${current.id}`} className="font-medium text-ink-800 hover:text-bronze-700 hover:underline">{name}</Link>
                  {name === current.leadTenantName && current.tenants.includes(' & ') && <span className="text-[10.5px] text-ink-400">lead</span>}
                </li>
              ))}
            </ul>
            {owing.map((o) => <div key={o.tenancyId} className="mt-2"><Badge value={`${formatMoney(o.owedMinor, o.currency)} overdue · ${o.daysOverdue}d`} tone="critical" /></div>)}
          </Card>
        )}
        {canDeposits && current && (
          <Card title="Deposit" actions={<Link to={`/app/money?sub=deposits&search=${encodeURIComponent(u.reference)}`} className="text-[12px] font-medium text-bronze-600 hover:underline">Open deposit</Link>}>
            {deposit.data ? (
              <KeyValue items={[
                { label: 'Held', value: <Money minor={deposit.data.amountMinor} currency={deposit.data.amountCurrency ?? u.marketRentCurrency} /> },
                { label: 'Status', value: <Badge value={deposit.data.status} /> },
                { label: 'Scheme', value: deposit.data.scheme ?? '—' },
                { label: 'Protected', value: deposit.data.protectedOn ? <DateText value={deposit.data.protectedOn} /> : 'Not yet' },
              ]} />
            ) : deposit.isLoading ? <SkeletonRows rows={2} cols={2} className="py-2" /> : <p className="py-4 text-center text-[12px] text-ink-400">No deposit recorded for this tenancy</p>}
          </Card>
        )}
        {budget && (
          <Card title={`Budget ${budget.year}`} actions={<Link to={`/app/properties/${propertyId}?tab=accounts`} className="text-[12px] font-medium text-bronze-600 hover:underline">Open budget</Link>}>
            <KeyValue items={[
              { label: 'Used for this flat', value: <Money minor={budgetUsed} currency={budget.currency} /> },
              { label: 'Share of budget per flat', value: <Money minor={budget.perUnit.budgetMinor} currency={budget.currency} /> },
              { label: 'Left for the property', value: <span className={budget.totals.varianceMinor < 0 ? 'text-red-600' : ''}><Money minor={budget.totals.varianceMinor} currency={budget.currency} /></span> },
            ]} />
          </Card>
        )}
      </div>
    </div>
  );
}

function ChangeoverTab({ r, propertyId }: { r: FlatRecord; propertyId: string }) {
  const c = r.changeover;
  if (!c) return <div className="surface px-4 py-10 text-center text-[13px] text-ink-500">No changeover in progress. One opens by itself when notice is given on the tenancy.</div>;
  const colour = PHASE_INFO[c.phase as Phase]?.colour ?? 'var(--color-violet-600)';
  const currentStep = c.steps.findIndex((s) => !s.done);
  return (
    <div className="space-y-2.5">
      <div className="surface p-5" style={{ boxShadow: `inset 3px 0 0 ${colour}` }}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold" style={{ color: colour }}>{c.phaseLabel}</div>
            <div className="text-[12.5px] text-ink-600">{c.nDone} of {c.steps.length} steps done{c.next ? ` · next: ${c.next.toLowerCase()}` : ''}</div>
          </div>
          <Link to={`/app/property?sub=changeovers&propertyId=${propertyId}`} className="inline-flex h-8 items-center gap-1 rounded-full bg-bronze-500 px-3.5 text-[12.5px] font-medium text-on-primary transition hover:bg-bronze-600">Open the changeover <ArrowRight className="h-3.5 w-3.5" /></Link>
        </div>
        {/* The strip of steps: done in the phase colour, the one under way outlined. */}
        <ol className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {c.steps.map((s, i) => (
            <li key={s.key} className={`rounded-xl border px-3 py-2 text-[12px] ${i === currentStep ? 'border-2' : ''}`}
              style={{ borderColor: s.done || i === currentStep ? colour : 'var(--color-ink-100)', background: s.done ? `color-mix(in oklab, ${colour} 10%, transparent)` : undefined }} aria-current={i === currentStep ? 'step' : undefined}>
              <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-400">Step {i + 1}{i === currentStep ? ' · now' : ''}</span>
              <span className={`block font-medium ${s.done ? 'text-ink-900' : 'text-ink-500'}`}>{s.label}</span>
            </li>
          ))}
        </ol>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Card title="Moving out">
          <KeyValue items={[
            { label: 'Tenancy', value: <Link to={`/app/tenancies/${c.outgoing.tenancyId}`} className="text-bronze-700 hover:underline">{c.outgoing.tenants || c.outgoing.reference}</Link> },
            { label: 'Status', value: <Badge value={c.outgoing.status} /> },
            { label: 'Ends', value: c.outgoing.endDate ? formatDate(c.outgoing.endDate) : '—' },
            { label: 'Move-out list', value: c.moveOut ? <Link to={`/app/tenancies/${c.outgoing.tenancyId}?checklist=out`} className="text-bronze-700 hover:underline">{c.moveOut.outstanding} of {c.moveOut.total} left</Link> : 'Not opened' },
          ]} />
        </Card>
        <Card title="Next in">
          <KeyValue items={[
            { label: 'Tenancy', value: c.incoming ? <Link to={`/app/tenancies/${c.incoming.tenancyId}`} className="text-bronze-700 hover:underline">{c.incoming.tenants || c.incoming.reference}</Link> : 'Nobody lined up yet' },
            { label: 'Starts', value: c.incoming?.startDate ? formatDate(c.incoming.startDate) : '—' },
            { label: 'Empty for', value: c.voidDays ? `${c.voidDays} days` : 'Still occupied' },
            { label: 'Target re-let', value: c.targetReletDate ? formatDate(c.targetReletDate) : '—' },
          ]} />
          {c.note && <p className="mt-3 text-[12px] italic text-ink-500">“{c.note}”</p>}
        </Card>
      </div>
    </div>
  );
}

function TenanciesTab({ r }: { r: FlatRecord }) {
  const navigate = useNavigate();
  type T = NonNullable<FlatRecord['tenancies']>[number];
  const cols: Column<T>[] = [
    { key: 'reference', label: 'Reference', lockedVisible: true, render: (t) => <span className="font-mono text-[12px] text-ink-700">{t.reference}</span> },
    { key: 'tenants', label: 'Tenant', render: (t) => <span className="font-medium text-ink-900">{t.tenants || '—'}{t.current && <span className="ml-1.5 text-[10.5px] font-normal text-emerald-700">now</span>}{t.upcoming && <span className="ml-1.5 text-[10.5px] font-normal text-sky-600">next</span>}</span> },
    { key: 'startDate', label: 'Start', render: (t) => (t.startDate ? formatDate(t.startDate) : '—') },
    { key: 'endDate', label: 'End', hideOnMobile: true, render: (t) => (t.endDate ? formatDate(t.endDate) : t.status === 'ENDED' ? '—' : 'ongoing') },
    { key: 'rentMinor', label: 'Rent', align: 'right', render: (t) => <Money minor={t.rentMinor} currency={t.rentCurrency} /> },
    { key: 'letBy', label: 'Let by', hideOnMobile: true, render: (t) => t.letBy ?? <span className="text-ink-300">—</span> },
    { key: 'status', label: 'Status', render: (t) => <Badge value={t.status} /> },
  ];
  const all = r.tenancies ?? [];
  const live = all.filter((t) => t.status !== 'ENDED' && t.status !== 'CANCELLED');
  const past = all.filter((t) => !live.includes(t));
  return (
    <div className="space-y-2.5">
      <section>
        <SectionHead n={live.length}>Current and next</SectionHead>
        <DataTable columns={cols} rows={live} page={1} onPage={() => undefined} rowKey={(t) => t.id} columnPicker={{ storageKey: 'unit.tenancies.columns' }} onRowClick={(t) => navigate(`/app/tenancies/${t.id}`)} emptyTitle="Nothing live in this flat" />
      </section>
      <section>
        <SectionHead n={past.length}>Past tenancies</SectionHead>
        <DataTable columns={cols} rows={past} page={1} onPage={() => undefined} rowKey={(t) => t.id} columnPicker={{ storageKey: 'unit.tenancies.columns' }} onRowClick={(t) => navigate(`/app/tenancies/${t.id}`)} emptyTitle="No past tenancies" emptyBody="Ended tenancies land here with their tenants and documents kept." />
      </section>
    </div>
  );
}

/** The flat's jobs: Outstanding by default, Archived through the Filter card's Show; "Report a job" is in the ⋯ menu. */
function WorksTab({ unitId }: { unitId: string }) {
  const navigate = useNavigate();
  const [show, setShow] = useState<'open' | 'done'>('open');
  const list = useQuery({ queryKey: ['/work-orders', { unitId, all: true }], queryFn: async () => (await api.get<WorkOrderRow[]>('/work-orders', { unitId, perPage: 200, sort: 'priority' })).data });
  const rows = list.data ?? [];
  const open = rows.filter((w) => w.isOpen); const done = rows.filter((w) => !w.isOpen);
  const cols: Column<WorkOrderRow>[] = [
    { key: 'title', label: 'Job', lockedVisible: true, render: (w) => (
      <div className="flex items-stretch gap-2.5">
        <span className="w-1 shrink-0 self-stretch rounded-full" style={{ background: workRowColour(w) }} aria-hidden />
        <div className="min-w-0"><div className="truncate font-medium text-ink-800">{w.title}</div><div className="font-mono text-[11px] text-ink-400">{w.reference} · {humanise(w.category) || 'Other'}</div></div>
      </div>
    ) },
    { key: 'priority', label: 'Priority', hideOnMobile: true, render: (w) => <Badge value={w.priority} /> },
    { key: 'reportedAt', label: 'Raised', hideOnMobile: true, render: (w) => (w.reportedAt ? formatDate(w.reportedAt) : '—') },
    { key: 'due', label: show === 'open' ? 'Due' : 'Done', render: (w) => (show === 'open' ? (w.slaResolveBy ? formatDate(w.slaResolveBy) : '—') : (w.completedAt ?? w.closedAt ? formatDate((w.completedAt ?? w.closedAt)!) : '—')) },
    { key: 'vendorName', label: 'Contractor', hideOnMobile: true, render: (w) => w.vendorName ?? w.assignedToName ?? <span className="text-ink-300">—</span> },
    { key: 'status', label: 'Status', render: (w) => <Badge value={w.status} /> },
  ];
  return (
    <DataTable columns={cols} rows={show === 'open' ? open : done} page={1} onPage={() => undefined} isLoading={list.isLoading} rowKey={(w) => w.id} onRowClick={(w) => navigate(`/app/work-orders/${w.id}`)}
      columnPicker={{ storageKey: 'unit.works.columns' }}
      filters={<FilterMenu value={{ show: [show] }} onChange={(n) => setShow(n.show?.[0] === 'done' ? 'done' : 'open')} groups={[
        { key: 'show', label: 'Show', single: true, defaultValue: 'open', options: [{ value: 'open', label: `Outstanding (${open.length})` }, { value: 'done', label: `Archived (${done.length})` }] },
      ]} />}
      emptyTitle={show === 'open' ? 'Nothing outstanding' : 'Nothing completed yet'} emptyBody={show === 'open' ? 'Repairs and compliance work raised against this flat appear here.' : 'Finished jobs stay on the flat\'s record.'} />
  );
}

function MarketingTab({ r, u, propertyId }: { r: FlatRecord; u: Record<string, any>; propertyId: string }) {
  const canCreate = useCan('listings.create');
  const canListings = useCan('listings.read');
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const l = r.flat.listing;
  const lettable = ['VACANT', 'NOTICE'].includes(u.status as string);
  return (
    <div className="space-y-2.5">
      {canListings && <>
      <Card title="The advert" actions={l ? <Link to={`/app/listings/${l.id}`} className="inline-flex items-center gap-1 text-[12px] font-medium text-bronze-600 hover:underline">Open the advert <ArrowRight className="h-3 w-3" /></Link> : undefined}>
        {l ? (
          <KeyValue items={[
            { label: 'Headline', value: l.headline },
            { label: 'Status', value: <Badge value={l.status} /> },
            { label: 'Advertised at', value: `${formatMoney(l.advertisedRentMinor, l.currency)} pcm` },
            { label: 'Available from', value: l.availableFrom ? formatDate(l.availableFrom) : 'Now' },
            { label: 'Published', value: l.publishedAt ? formatDate(l.publishedAt) : 'Not yet' },
          ]} />
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <p className="min-w-0 flex-1 text-[12.5px] text-ink-500">This flat is not advertised.{!lettable ? ' It can go on the market once it is vacant or on notice.' : ''}</p>
            {canCreate && lettable && <Button size="sm" icon="Megaphone" onClick={() => setCreating(true)}>Put on the market</Button>}
          </div>
        )}
      </Card>
      <UnitLettingsCard unit={u} propertyId={propertyId} reservation={r.linked.reservation} />
      </>}
      {/* When the flat can be viewed (viewings.read; docs/plans/viewing-first-onboarding.md). */}
      <UnitViewingsCard unitId={u.id as string} />
      {creating && <ListingForm open unit={u} onClose={() => { setCreating(false); void qc.invalidateQueries({ queryKey: ['flat-record', u.id] }); void qc.invalidateQueries({ queryKey: ['/listings'] }); }} />}
    </div>
  );
}

function ComplianceTab({ r, propertyId, focus, onFocus, onRecord }: { r: FlatRecord; propertyId: string; focus: string | null; onFocus: (type: string | null) => void; onRecord?: (type: string) => void }) {
  const comp = r.compliance;
  if (!comp) return <div className="surface px-4 py-10 text-center text-[13px] text-ink-500">No certificate types apply to this flat.</div>;
  const row = { key: comp.rowKey, kind: 'UNIT' as const, unitId: r.flat.id, label: r.flat.reference, cells: comp.cells };
  const open = focus ? comp.cells[focus] : null;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 text-[12.5px] text-ink-500">Statutory certificates for this flat. Open one to see it with the ones it replaced, or record it where there is none.</p>
        <Link to={`/app/properties/${propertyId}?tab=management&sub=compliance`} className="inline-flex items-center gap-1 text-[12px] font-medium text-bronze-600 hover:underline">See the whole building <ArrowRight className="h-3 w-3" /></Link>
      </div>
      <ul className="space-y-2">
        {comp.columns.map((col) => {
          const cell = comp.cells[col.key]!;
          const tone = CELL_TONE[cell.status] ?? CELL_TONE.NOT_RECORDED!;
          const has = !!cell.certificateId;
          const act = () => (has ? onFocus(col.key) : onRecord?.(col.key));
          return (
            <li key={col.key}>
              <button type="button" onClick={act} disabled={!has && !onRecord} className="surface flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left transition hover:shadow-pop disabled:cursor-default disabled:hover:shadow-none" style={{ boxShadow: `inset 3px 0 0 ${tone.color}` }}>
                <ShieldCheck className="h-4 w-4 shrink-0 text-ink-300" />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2 text-[13px] font-medium text-ink-900">{col.label}
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-[2px] text-[11px] font-medium" style={{ color: tone.color, background: `color-mix(in oklab, ${tone.color} 12%, transparent)` }}><i className="h-1.5 w-1.5 rounded-full" style={{ background: tone.color }} />{tone.label}</span>
                  </span>
                  <span className="block text-[11.5px] text-ink-500">
                    Renews every {col.intervalMonths} months{cell.issuedOn ? ` · issued ${formatDate(cell.issuedOn)}` : ''}{cell.expiresOn ? ` · expires ${formatDate(cell.expiresOn)}` : ''}{cell.vendorName ? ` · ${cell.vendorName}` : ''}{cell.historyCount ? ` · ${cell.historyCount} earlier` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] font-medium text-bronze-600">{has ? 'Open' : onRecord ? 'Record it' : ''}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {open?.certificateId && focus && <CertificateSheet row={row} type={focus} typeLabel={comp.columns.find((c) => c.key === focus)?.label ?? focus} cell={open} onClose={() => onFocus(null)} onRenew={() => { onFocus(null); onRecord?.(focus); }} />}
      {!comp.columns.length && <div className="surface px-4 py-8 text-center text-[12.5px] text-ink-400"><ClipboardCheck className="mx-auto mb-2 h-5 w-5" />No certificate types apply to this flat.</div>}
    </div>
  );
}
